import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Box, Button, Flex, Grid, HStack, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { PasswordControl } from "../../../components/Controls";
import { useAuth } from "../../../plugins/CookieAuthPlugin";
import { toastGenerator } from "../../../utils/helper";
import { invalidateUserQuery, useChangePasswordeUser } from "../../../utils/hooks/users";

export default function ChangePassword() {
  const toast = useToast();
  const {
    user: { statusLogin },
    logout,
  } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleSubmit, control, getValues, reset } = useForm();
  const { t } = useTranslation();
  const changePassword = useChangePasswordeUser({
    onSuccess(data) {
      let status = data.header.result;
      toast(
        toastGenerator({
          status: status ? "success" : "warning",
          message: data.body.message ? data.body.message : data.body,
        }),
      );
      invalidateUserQuery();
      reset({});
      status && onOpen();
    },
  });
  const { pathname } = useLocation();
  const parentUrl = `/${pathname.split("/")[1]}`;
  const history = useHistory();
  useEffect(() => {
    statusLogin === 1 &&
      toast({
        title: t("label.consider_changing_your_pawword"),
        description: t("label.please_change_password_before_using_system"),
        status: "info",
        fontSize: "sm",
        duration: 3000,
        variant: "subtle",
        position: "top",
      });
    //eslint-disable-next-line
  }, []);
  const onSubmit = (values) => {
    let formData = new FormData();
    formData.append("oldPassword", values.oldPassword);
    formData.append("newPassword", values.newPassword);
    changePassword.mutate(formData);
  };

  function StayOnPage() {
    return (
      <AlertDialog motionPreset="slideInBottom" onClose={onClose} isOpen={isOpen} isCentered closeOnEsc={false}>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" textAlign={"center"}>
            {t("you_are_about_to_be_logged_out")}
          </AlertDialogHeader>
          <AlertDialogBody justifyContent={"center"} alignItems="center">
            <Flex justifyContent={"center"}>
              <Button
                colorScheme="brand"
                onClick={() => {
                  history.push(`${parentUrl}/list`);
                }}
                ml={3}
              >
                {t("stay_login")}
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  logout();
                }}
                ml={3}
              >
                {t("log_me_out")}
              </Button>
            </Flex>
          </AlertDialogBody>
          <AlertDialogFooter />
        </AlertDialogContent>
      </AlertDialog>
    );
  }
  return (
    <Box bg="white" minH="full" rounded="md">
      {StayOnPage()}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid templateColumns="auto max-content" p="3" mb="3" boxShadow="sm">
          <Grid templateColumns="max-content" gap="2" alignContent="center">
            <Text ml="2" fontSize="lg" fontWeight="semibold">
              {t("change_password")}
            </Text>
          </Grid>
          <Grid>
            <HStack>
              <Button type="submit" colorScheme="redAlt" variant="duotone">
                {t("label.update")}
              </Button>
            </HStack>
          </Grid>
        </Grid>
        <Grid py="2" templateColumns="repeat(3,1fr)" gap="4" p="3">
          <VStack spacing="6">
            <PasswordControl
              name="oldPassword"
              label={t("label.current_password")}
              placeholder={t("label.enter")}
              control={control}
              isRequired
              rules={{
                minLength: {
                  value: 8,
                  message: t("label.password_must_have_at_least_8_characters"),
                },
              }}
            />
            <PasswordControl
              name="newPassword"
              label={t("label.new_password")}
              placeholder={t("label.enter")}
              control={control}
              isRequired
              rules={{
                pattern: {
                  value: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
                  message: t("label.password_must_have_one_special_character"),
                },
              }}
            />
            <PasswordControl
              name="reTypePassword"
              label={t("label.retype_password")}
              placeholder={t("label.enter")}
              control={control}
              rules={{
                validate: (value) => {
                  return value === getValues("newPassword") || t("label.password_does_not_match");
                },
              }}
            />
          </VStack>
        </Grid>
      </form>
    </Box>
  );
}
